import {imageUrlFor} from "../lib/image-url"
import {buildImageObj} from "../lib/helpers"

export const getJsonLD = (
  {
    site,
    company,
    description,
    title,
    url,
    imageUrl,
    category,
    tag,
    page,
    post,
    pathname
  }) => {

  const isHomepage = pathname === '/'
  const items = []
  category && items.push({
    pathname: `${category.slug}/`,
    name: category.title,
  })
  post && items.push({
    pathname: `${post.pathname}`,
    name: post.title,
  })
  page && items.push({
    pathname: `${page.slug}/`,
    name: page.title,
  })
  tag && items.push({
    pathname: `${tag.slug}/`,
    name: tag.title,
  })

  const baseGraphs = [
    organization({
      name: company.name,
      email: company.email,
      description: site.description,
      siteUrl: site.siteUrl,
      imageUrl: imageUrlFor(buildImageObj(site.ogImage))
        .width(1200)
        .height(630)
        .auto('format')
        .quality(75)
        .fit('clip')
        .url(),
      logoUrl: imageUrlFor(buildImageObj(site.logoImage))
        .width(250)
        .height(68)
        .auto('format')
        .quality(75)
        .fit('clip')
        .url(),
    }),
    logo(site),
    siteImage(site),
    website(site),
    webpage({
      site,
      url,
      title,
      description,
      showBreadCrumb: !isHomepage
    }),
    primaryImage({url, imageUrl}),
  ]

  const breadCrumbs = !isHomepage && [breadcrumb({site, items})] || []

  const postGraphs = post && [
    article({site, post, category, url}),
    author({name: post.author, siteUrl: site.siteUrl}),
  ] || []


  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [...baseGraphs, ...breadCrumbs, ...postGraphs],
  })
}

const organization = ({name, email, description, siteUrl}) => (
  {
    "@context": "http://schema.org",
    "@id": `${siteUrl}/#organization`,
    "@type": "Organization",
    "name": name,
    "url": siteUrl,
    "description": description,
    "email": email,
    "founder": "Solene Rouvier",
    "foundingDate": "2018-03-07",
    "foundingLocation": "Lyon, FR",
    "image": {
      "@id": `${siteUrl}/#image`,
    },
    "logo": {
      "@id": `${siteUrl}/#logo`,
    },
    "sameAs": [
      "https://twitter.com/MoneyloBlog",
      "https://facebook.com/moneyloblog",
      "https://instagram.com/moneyloblog",
      "https://pinterest.com/moneyloblog",
    ],
  }
)

const website = site => (
  {
    "@context": "http://schema.org",
    "@type": "WebSite",
    "@id": `${site.siteUrl}/#website`,
    url: site.siteUrl,
    name: site.name,
    inLanguage: "fr-FR",
    description: site.description,
    publisher: {
      "@id": `${site.siteUrl}/#organization`,
    },
  }
)

const webpage = ({site, url, title, description, showBreadCrumb}) => (
  {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    "url": url,
    "name": title,
    "isPartOf": {
      "@id": `${site.siteUrl}/#website`,
    },
    "inLanguage": "fr-FR",
    "primaryImageOfPage": {
      "@id": `${url}#primaryimage`,
    },
    "description": description,
    "breadcrumb": showBreadCrumb && {
      "@id": `${url}#breadcrumb`,
    },
    "potentialAction": [{
      "@type": "ReadAction",
      "target": [
        url,
      ],
    }],
  }
)

const breadcrumb = ({site: {siteUrl}, items = []}) => (
  {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${siteUrl}/#breadcrumb`,
    name: `Fil d'Ariane`,
    "itemListElement": [
      breadcrumbListItem({
        url: siteUrl,
        position: 0,
        name: 'Accueil',
      }),
    ].concat(items ? items.map((item, index) => (
      breadcrumbListItem({
        url: `${siteUrl}${item.pathname}`,
        name: item.name,
        position: index + 1,
      })
    )) : []),
  }
)

const breadcrumbListItem = ({url, name, position}) => (
  {
    "@type": "ListItem",
    "item": {
      "@id": url,
      "@type": "WebPage",
      "name": name,

    },
    "position": position,
  }
)


const primaryImage = ({imageUrl, url}) => (
  {
    "@type": "ImageObject",
    "@id": `${url}#primaryimage`,
    "inLanguage": "fr-FR",
    "url": imageUrl,
    "width": 1200,
    "height": 600,
  }
)

const logo = site => (
  {
    "@type": "ImageObject",
    "@id": `${site.siteUrl}/#logo`,
    "inLanguage": "fr-FR",
    "url": imageUrlFor(buildImageObj(site.logoImage))
      .width(250)
      .height(68)
      .auto('format')
      .quality(75)
      .fit('clip')
      .url(),
    "width": 250,
    "height": 68,
  }
)

const siteImage = site => (
  {
    "@type": "ImageObject",
    "@id": `${site.siteUrl}/#image`,
    "inLanguage": "fr-FR",
    "url": imageUrlFor(buildImageObj(site.ogImage))
      .width(1200)
      .height(630)
      .auto('format')
      .quality(75)
      .fit('clip')
      .url(),
    "width": 1200,
    "height": 630,
  }
)

const author = ({siteUrl, name}) => (
  {
    "@type": "Person",
    "@id": `${siteUrl}/#author`,
    name: name,
  }
)


const article = ({site: {siteUrl}, post, category, url}) => (
  {
    "@context": "http://schema.org",
    "@type": "Article",
    name: post.title,
    headline: post.title,
    author: {
      "@id": `${siteUrl}/#author`,
    },
    creator: {
      "@id": `${siteUrl}/#organization`,
    },
    copyrightHolder: {
      "@id": `${siteUrl}/#organization`,
    },
    copyrightYear: new Date(post.publishedAt).getYear() + 1900,
    publisher: {
      "@id": `${siteUrl}/#organization`,
    },
    mainEntityOfPage: url,
    url: url,
    inLanguage: "fr-FR",
    datePublished: post.publishedAt,
    dateModified: post._updatedAt,
    image: {
      "@id": `${url}#primaryimage`,
    },
    keywords: post.tags,
    articleSection: category.title,
    speakable: {
      "@type": "SpeakableSpecification",
      "cssSelector": [
        ".content",
      ],
    },
  }
)

export const howTo = ({name, id, description, url, steps = []}) => (
  JSON.stringify({
    "@type": "HowTo",
    "@id": `${url}#${id}`,
    "name": name,
    "mainEntityOfPage": {
      "@id": `${url}#article`,
    },
    "description": description,
    "step": steps.map(({url, name, text}) => howToStep({url, name, text})),
    "inLanguage": "fr-FR",
  })
)

const howToStep = ({url, name, text}) => (
  {
    "@type": "HowToStep",
    "url": url,
    "name": name,
    "itemListElement": [
      {
        "@type": "HowToDirection",
        "text": text,
      },
    ],
  }
)
